import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { backgroundRequest } from 'app/shared/services/loading';
import { environment } from 'environments/environment';

/**
 * Header bell inbox, backed by GET /api/UserNotifications (rows are created
 * by the backend when something relevant happens to the user).
 *
 * Every call here is marked as background work. The bell is ambient: reading
 * the inbox, ticking one row read or clearing the lot are all things that
 * happen *around* whatever the user came to do. Left on the default path they
 * raise the full-screen loading splash, so a click on the bell looked like the
 * app had navigated away and come back.
 *
 * Every change publishes a NEW list. The bell keeps the list in a signal, and a
 * signal set to the array it already holds does not notify: the old in-place
 * splice/assign left a deleted row on screen and the unread badge unchanged
 * until the next poll. Changes are optimistic and undone item by item when the
 * request fails, so a refresh that landed meanwhile is not thrown away.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private _apiUrl = environment.apiUrlPrefix;
  private _notificationsUrl = 'UserNotifications';
  private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
  /** The list last published, the base every change is applied to. */
  private _current: Notification[] = [];

  /** Unread notifications older than the loaded list, which the badge still counts. */
  readonly unreadBeyondList = signal(0);

  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get the current user's notifications, with the unread ones the list leaves out.
   *
   * The list stops at the latest 50, so a badge counted from it alone missed older
   * unread ones. The total comes from its own endpoint; if that one fails the list
   * still loads and the badge falls back to what the list holds.
   */
  getAll(): Observable<Notification[]> {
    const list = this._httpClient.get<Notification[]>(`${this._apiUrl}${this._notificationsUrl}`, {
      context: backgroundRequest(),
    });
    const unread = this._httpClient
      .get<{
        count: number;
      }>(`${this._apiUrl}${this._notificationsUrl}/unread-count`, { context: backgroundRequest() })
      .pipe(
        map(({ count }) => count),
        catchError(() => of(null as number | null))
      );

    return forkJoin({ list, unread }).pipe(
      map(({ list: notifications, unread: total }) => {
        const loaded = notifications ?? [];
        const unreadLoaded = loaded.filter((item) => !item.read).length;
        this.unreadBeyondList.set(total === null ? 0 : Math.max(0, total - unreadLoaded));
        this._publish(loaded);
        return loaded;
      })
    );
  }

  /**
   * Flip the read state of a notification, showing the change at once and
   * undoing it if the server refuses.
   */
  toggleRead(notification: Notification): Observable<Notification> {
    return this._setReadOnServer(notification, !notification.read);
  }

  /**
   * Leaves a notification in the given read state. The endpoint sets the state
   * rather than toggling it, so a tab that is behind cannot turn a read row unread.
   */
  private _setReadOnServer(notification: Notification, read: boolean): Observable<Notification> {
    const previous = notification.read;
    this._setRead(notification.id, read);

    return this._httpClient
      .put<{
        read: boolean;
      }>(
        `${this._apiUrl}${this._notificationsUrl}/${notification.id}/read`,
        { read },
        { context: backgroundRequest() }
      )
      .pipe(
        map(({ read }) => {
          this._setRead(notification.id, read);
          return { ...notification, read };
        }),
        catchError((error) => {
          this._setRead(notification.id, previous);
          return throwError(() => error);
        })
      );
  }

  /**
   * Mark a notification read when it is opened. Nothing is sent for one that is
   * already read.
   */
  markAsRead(notification: Notification): Observable<Notification> {
    return notification.read ? of(notification) : this._setReadOnServer(notification, true);
  }

  /**
   * Delete a notification, removing the row at once and putting it back if the
   * server refuses.
   */
  delete(notification: Notification): Observable<boolean> {
    this._publish(this._current.filter((item) => item.id !== notification.id));

    return this._httpClient
      .delete(`${this._apiUrl}${this._notificationsUrl}/${notification.id}`, {
        context: backgroundRequest(),
      })
      .pipe(
        map(() => {
          // A poll that answered before the delete committed brought the row back.
          if (this._current.some((item) => item.id === notification.id)) {
            this._publish(this._current.filter((item) => item.id !== notification.id));
          }
          return true;
        }),
        catchError((error) => {
          if (!this._current.some((item) => item.id === notification.id)) {
            // The panel sorts by time, so where it goes back in does not matter.
            this._publish([...this._current, notification]);
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Mark all notifications as read, undoing it for the ones that were unread if
   * the server refuses.
   */
  markAllAsRead(): Observable<boolean> {
    const unreadIds = new Set(this._current.filter((item) => !item.read).map((item) => item.id));
    const unreadBeyond = this.unreadBeyondList();
    // The server marks every unread row, the ones past the list too.
    this.unreadBeyondList.set(0);
    this._publish(this._current.map((item) => (item.read ? item : { ...item, read: true })));

    return this._httpClient
      .put<{
        updated: number;
      }>(`${this._apiUrl}${this._notificationsUrl}/mark-all-read`, {}, { context: backgroundRequest() })
      .pipe(
        map(() => {
          // Same for a poll that answered before the update committed.
          if (this._current.some((item) => unreadIds.has(item.id) && !item.read)) {
            this._publish(
              this._current.map((item) => (unreadIds.has(item.id) ? { ...item, read: true } : item))
            );
          }
          return true;
        }),
        catchError((error) => {
          this.unreadBeyondList.set(unreadBeyond);
          this._publish(
            this._current.map((item) => (unreadIds.has(item.id) ? { ...item, read: false } : item))
          );
          return throwError(() => error);
        })
      );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  private _setRead(id: string, read: boolean): void {
    this._publish(
      this._current.map((item) => (item.id === id && item.read !== read ? { ...item, read } : item))
    );
  }

  private _publish(notifications: Notification[]): void {
    this._current = notifications;
    this._notifications.next(notifications);
  }
}
